import React, { useState, useRef, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Link } from "react-router-dom";
import { FaPhone, FaTag } from "react-icons/fa";
import ILeadData from "../../models/LeadData.model";
import toast from "react-hot-toast";
import {
  bulkUpdateAssignees,
  fetchLeads,
  IKanbanState,
} from "../../store/slices/kanbanSlice";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import Button, { ButtonType } from "../Assets/Button";
import ListViewSkeleton from "../Skeleton/ListViewSkeleton";
import BulkAssignee from "../BulkAssignee/BulkAssigneeUpdate";
import { isEqual } from "lodash";

interface ListViewProps {
  sanitizeFilters: (filters: any) => any;
}

const FaTagIcon = FaTag as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaPhoneIcon = FaPhone as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

interface LeadRowProps {
  card: ILeadData;
  columnName: string;
  isSelected: boolean;
  onSelect: (leadId: string, isSelected: boolean) => void;
}

interface LeadRowProps {
  card: ILeadData;
  columnName: string;
  isSelected: boolean;
  onSelect: (leadId: string, isSelected: boolean) => void;
}

const LeadRow: React.FC<LeadRowProps> = ({
  card,
  columnName,
  isSelected,
  onSelect,
}) => (
  <div className="grid grid-cols-[50px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] items-center border-b hover:bg-gray-50 transition-colors duration-200 p-3">
    <div className="flex items-center">
      <input
        type="checkbox"
        checked={isSelected}
        onChange={(e) => onSelect(card?._id ?? "", e.target.checked)}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
    <div className="flex items-center">
      <Link
        to={`/lead-details/${card._id}`}
        state={{ card, columnName }}
        className="text-gray-500 hover:text-blue-700 flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 mr-2"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
        {card?.name?.capitalizeWords()}
      </Link>
    </div>
    <div className="flex items-center gap-2">
      <FaPhoneIcon className="text-gray-500" />
      {card?.mobileNumber}
    </div>
    <div>{card?.contactPersonName?.capitalizeWords()}</div>
    <div>{card?.address?.state?.capitalizeWords() ?? ""}</div>
    <div>{card?.address?.district?.capitalizeWords() ?? ""}</div>
    <div className="flex items-center gap-2">
      {card?.assignee?.username ? (
        <>
          <FaTagIcon className="text-gray-500" />
          {card?.assignee?.username?.capitalizeWords()}
        </>
      ) : (
        <span className="text-gray-500">Unassigned</span>
      )}
    </div>
    <div className="text-gray-600">{card?.type?.capitalizeWords()}</div>
    <div className="text-gray-600">{columnName?.capitalizeWords()}</div>
  </div>
);

const OptimizedListView: React.FC<ListViewProps> = ({ sanitizeFilters }) => {
  const dispatch: RootDispatch = useDispatch();
  const {
    listLeads = [],
    listHasMore = true,
    listLoading,
  } = useSelector((state: RootState) => state.kanban as IKanbanState);
  const roles = useSelector((state: RootState) => state.common.roles);

  const parentRef = useRef(null);
  const rowHeight = 58;

  const virtualizer = useVirtualizer({
    count: listLeads.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 5,
  });

  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [bulkAssignee, setBulkAssignee] = useState("");
  const lastFilters = useSelector((s: RootState) => s.kanban.lastFilters);
  const filters = useSelector((s: RootState) => s.kanbanFilters);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const sanitized = sanitizeFilters(filters ?? {});
    const sanitizedLastFilters = sanitizeFilters(lastFilters ?? {});
    if (!lastFilters || !isEqual(sanitized, sanitizedLastFilters)) {
      try {
        dispatch(
          fetchLeads({
            page: currentPage,
            limit: pageSize,
            filters: filters,
            append: false, // Always replace because we're paginating
          }),
        );
        setSelectedLeads([]);
      } catch (error) {
        console.error("Failed to fetch leads:", error);
      }
    }
  }, [filters, currentPage, pageSize, dispatch]);

  //for pagination
  useEffect(() => {
    try {
      dispatch(
        fetchLeads({
          page: currentPage,
          limit: pageSize,
          filters: filters,
          append: false, // Always replace because we're paginating
        }),
      );
      setSelectedLeads([]);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    }
  }, [currentPage, pageSize, dispatch]);

  const handleSelectLead = (leadId: string, isSelected: boolean) => {
    setSelectedLeads((prev) =>
      isSelected ? [...prev, leadId] : prev.filter((id) => id !== leadId),
    );
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedLeads(listLeads.map((lead: ILeadData) => lead._id ?? ""));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleBulkAssigneeUpdate = async () => {
    if (!bulkAssignee) {
      toast.error("Please select an assignee");
      return;
    }
    if (selectedLeads.length === 0) {
      toast.error("Please select at least one lead");
      return;
    }
    try {
      await dispatch(
        bulkUpdateAssignees({
          leadIds: selectedLeads,
          assigneeId: bulkAssignee,
        }),
      ).unwrap();
      setSelectedLeads([]);
      setBulkAssignee("");
    } catch (error) {
      console.error("Bulk update failed:", error);
    }
  };

  if (listLoading) {
    return <ListViewSkeleton />;
  }

  return (
    <div className="w-full overflow-x-auto text-xs" data-testid="list-view">
      <BulkAssignee
        selectedCount={selectedLeads.length}
        assigneeList={[
          ...(roles.userBasedOnRoles ?? []),
          ...(roles.manager ? [roles.manager] : []),
        ]}
        selectedAssignee={bulkAssignee}
        setSelectedAssignee={setBulkAssignee}
        onUpdate={handleBulkAssigneeUpdate}
        onCancel={() => setSelectedLeads([])}
      />
      {/* Virtualized List Container */}
      <div className="relative h-[70vh] overflow-y-auto" ref={parentRef}>
        {/* Table Header */}
        <div className="grid grid-cols-[50px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] sticky top-0 bg-gray-100 z-10 p-3 border-b">
          <div className="text-left font-semibold">
            <input
              type="checkbox"
              checked={
                selectedLeads.length === listLeads.length &&
                listLeads.length > 0
              }
              onChange={handleSelectAll}
            />
          </div>
          {[
            "Name",
            "Mobile Number",
            "Contact Person",
            "State",
            "District",
            "Assignee",
            "Source Type",
            "Status",
          ].map((heading) => (
            <div key={heading} className="text-left font-semibold">
              {heading}
            </div>
          ))}
        </div>
        {/* Virtualized Rows */}
        <div
          className="relative"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const card = listLeads[virtualItem.index];
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                className="absolute w-full"
                style={{
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <LeadRow
                  card={card}
                  columnName={card.status || ""}
                  isSelected={selectedLeads.includes(card._id ?? "")}
                  onSelect={handleSelectLead}
                />
              </div>
            );
          })}
        </div>

        {listLeads.length === 0 && (
          <div className="text-center py-4 text-gray-500">No leads found.</div>
        )}
      </div>
      <div className="flex justify-center items-center gap-2 p-4">
        <Button
          type={ButtonType.Cancel}
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </Button>

        <span className="px-3 py-1 border rounded">Page {currentPage}</span>

        <Button
          type={ButtonType.Extra}
          onClick={() => {
            if (listHasMore) setCurrentPage((p) => p + 1);
          }}
          disabled={!listHasMore}
        >
          Next
        </Button>
        <div className="flex items-center gap-2">
          <label htmlFor="pageSize" className="text-sm">
            Rows per page:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1); // reset to first page when size changes
            }}
            className="border rounded p-1 text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default OptimizedListView;
